// 페이지별 title/description/canonical/OG 메타와 JSON-LD를 한 곳에서 주입하는 Helmet 래퍼
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://mrdevello.com';
const SITE_NAME = '미스터디벨로';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.webp`;

type SeoProps = {
	title: string; // 페이지 제목 (사이트명은 자동으로 붙음)
	titleIsComplete?: boolean; // 승인된 브랜드 제목은 접미어 없이 그대로 사용
	description: string;
	keywords?: string;
	path: string; // '/faq' 처럼 슬래시로 시작하는 경로
	image?: string;
	jsonLd?: Record<string, unknown>;
	noindex?: boolean;
};

const toAbsolute = (url: string) =>
	url.startsWith('http') ? url : `${SITE_URL}${url}`;

const Seo = ({ title, titleIsComplete = false, description, keywords, path, image, jsonLd, noindex = false }: SeoProps) => {
	const fullTitle = titleIsComplete ? title : `${title} | ${SITE_NAME}`;
	const url = `${SITE_URL}${path}`;
	const ogImage = image ? toAbsolute(image) : DEFAULT_IMAGE;

	return (
		<Helmet defer={false}>
			<title>{fullTitle}</title>
			<meta name="description" content={description} />
			{keywords && <meta name="keywords" content={keywords} />}
			<meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
			{!noindex && <link rel="canonical" href={url} />}
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={url} />
			<meta property="og:image" content={ogImage} />
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={ogImage} />
			<meta name="apple-mobile-web-app-title" content={fullTitle} />
			{!noindex && <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@graph': [
          { '@type': 'WebPage', '@id': `${url}#webpage`, url, name: fullTitle, description, inLanguage: 'ko-KR', isPartOf: { '@id': `${SITE_URL}/#website` }, about: { '@id': `${SITE_URL}/#organization` }, ...(path === '/' ? {} : { breadcrumb: { '@id': `${url}#breadcrumb` } }) },
          ...(path === '/' ? [] : [{ '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`, itemListElement: [
            { '@type': 'ListItem', position: 1, name: SITE_NAME, item: `${SITE_URL}/` },
            ...(path.startsWith('/portfolio/') ? [{ '@type': 'ListItem', position: 2, name: '포트폴리오', item: `${SITE_URL}/portfolio` }] : []),
            { '@type': 'ListItem', position: path.startsWith('/portfolio/') ? 3 : 2, name: title, item: url }
          ] }]),
          ...(jsonLd ? [{ ...jsonLd, '@id': `${url}#content`, mainEntityOfPage: { '@id': `${url}#webpage` } }] : [])
        ]
      }).replace(/</g, '\\u003c')}</script>}
		</Helmet>
	);
};

export default Seo;
